/**
 * TanStack Query hooks of the `/fleet` module. Keys start with `"fleet"` so the realtime layer (`./socket.ts`)
 * can invalidate by entity (`["fleet", "orders", id]`) and the OneSphere queries stay untouched.
 */
import { useQuery } from "@tanstack/react-query";
import { fleetHeaders, fleetUrl, onefleet } from "./client";
import { z } from "zod";
import {
  ErpExpandSchema,
  HealthSchema,
  ReadinessSchema,
  VersionSchema,
  type Readiness,
} from "./types";

export const fleetKeys = {
  all: ["fleet"] as const,
  system: () => ["fleet", "system"] as const,
  health: () => ["fleet", "system", "health"] as const,
  readiness: () => ["fleet", "system", "readiness"] as const,
  version: () => ["fleet", "system", "version"] as const,
};

/** `/healthz` — the OneFleet API process answers through the edge. */
export function useOneFleetHealth(enabled = true) {
  return useQuery({
    queryKey: fleetKeys.health(),
    queryFn: async () => HealthSchema.parse(await onefleet.get("/healthz")),
    enabled,
    staleTime: 30_000,
    retry: 0,
  });
}

/** `/readyz` — Postgres and Valkey behind it; a 503 still carries the detail, so it is read rather than thrown. */
export async function fetchReadiness(): Promise<Readiness> {
  const res = await fetch(fleetUrl("/readyz"), { headers: fleetHeaders() });
  if (res.status !== 200 && res.status !== 503)
    throw new Error(`OneFleet ${res.status}: ${await res.text()}`);
  return ReadinessSchema.parse(await res.json());
}

export function useOneFleetReadiness(enabled = true) {
  return useQuery({
    queryKey: fleetKeys.readiness(),
    queryFn: fetchReadiness,
    enabled,
    staleTime: 30_000,
    retry: 0,
  });
}

/** `/version` — package version, git revision and start time of the API. */
export function useOneFleetVersion(enabled = true) {
  return useQuery({
    queryKey: fleetKeys.version(),
    queryFn: async () => VersionSchema.parse(await onefleet.get("/version")),
    enabled,
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

/** `/int/v1/iam/me` — who OneFleet takes the signed-in person for: memberships (companies), roles, permissions (WP-B2). */
export const MeSchema = z.object({
  kind: z.string(),
  id: z.string().nullable(),
  display: z.string(),
  company_id: z.string().nullable(),
  company_public_id: z.string().nullable(),
  roles: z.array(z.string()),
  realm_roles: z.array(z.string()),
  permissions: z.array(z.string()),
  memberships: z.array(
    z.object({
      company_id: z.string(),
      company_public_id: z.string(),
      slug: z.string(),
      name: z.string(),
      roles: z.array(z.string()),
    }),
  ),
  sandbox: z.boolean(),
  company_required: z.boolean(),
});
export type OneFleetMe = z.infer<typeof MeSchema>;

export function useOneFleetMe(enabled = true, company?: string) {
  return useQuery({
    queryKey: [...fleetKeys.all, "iam", "me", company ?? ""],
    queryFn: async () =>
      MeSchema.parse(await onefleet.get("/int/v1/iam/me", { company })),
    enabled,
    staleTime: 60_000,
    retry: 0,
  });
}

/** `/int/v1/fleet-ops/vehicles` — the vehicle register (WP-B3): envelope `{data, meta{total, next_cursor}, links}`. */
export const VehicleSchema = z.object({
  id: z.string(),
  plate_number: z.string(),
  internal_id: z.string().nullable(),
  vin: z.string().nullable(),
  display_name: z.string().nullable(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  year: z.number().nullable(),
  type: z.string(),
  status: z.string(),
  online: z.boolean(),
  odometer: z.number(),
  odometer_unit: z.string(),
  capacity_weight_kg: z.number().nullable().optional(),
  cost_center_code: z.string().nullable(),
  location: z
    .object({
      type: z.literal("Point"),
      coordinates: z.tuple([z.number(), z.number()]),
    })
    .nullable(),
  last_position_at: z.string().nullable(),
  version: z.number(),
  updated_at: z.string(),
  custom_fields: z.record(z.string(), z.unknown()).default({}),
  erp: ErpExpandSchema.optional(), // with `expand=erp` (D-90)
});
export type OneFleetVehicle = z.infer<typeof VehicleSchema>;
export const VehiclePageSchema = z.object({
  data: z.array(VehicleSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    cursor: z.string().nullable(),
    next_cursor: z.string().nullable(),
  }),
});
export type VehiclePage = z.infer<typeof VehiclePageSchema>;

export function useOneFleetVehicles(
  opts: {
    q?: string;
    status?: string;
    cursor?: string;
    limit?: number;
    company?: string;
    enabled?: boolean;
    expand?: string;
  } = {},
) {
  const {
    q,
    status,
    cursor,
    limit = 25,
    company,
    enabled = true,
    expand,
  } = opts;
  return useQuery({
    queryKey: [
      ...fleetKeys.all,
      "vehicles",
      {
        q: q ?? "",
        status: status ?? "",
        cursor: cursor ?? "",
        limit,
        company: company ?? "",
        expand: expand ?? "",
      },
    ],
    queryFn: async () =>
      VehiclePageSchema.parse(
        await onefleet.get("/int/v1/fleet-ops/vehicles", {
          company,
          query: {
            q,
            "filter[status]": status,
            cursor,
            limit,
            sort: "plate_number",
            expand,
          },
        }),
      ),
    enabled,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}
